import React, { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import colors from "../../../assets/theme/colors";
import CareProfileBackButton from "../../UI/CareProfileBackButton";
import { LogFormShell } from "../../UI";
import CareKnowledgeCaptureCard from "../../../features/careSourceProcessing/CareKnowledgeCaptureCard";

export const PROFILE_ADD_INTENTS = {
  TIMELINE: "timeline",
  KNOWLEDGE: "knowledge",
  MEDICATION: "medication",
  GOAL: "goal",
  DOCUMENT: "document",
};

const ADD_OPTIONS = [
  {
    id: PROFILE_ADD_INTENTS.TIMELINE,
    label: "Something that happened",
    description: "Add an event or observation to the Timeline.",
    icon: TimelineOutlinedIcon,
  },
  {
    id: PROFILE_ADD_INTENTS.KNOWLEDGE,
    label: "Something important to know",
    description: "Share lasting care information in your own words.",
    icon: MenuBookOutlinedIcon,
  },
  {
    id: PROFILE_ADD_INTENTS.MEDICATION,
    label: "Medication",
    description: "Open the existing Medication Manager.",
    icon: MedicationOutlinedIcon,
  },
  {
    id: PROFILE_ADD_INTENTS.GOAL,
    label: "Goal",
    description: "Review or add a caregiver-confirmed goal.",
    icon: FlagOutlinedIcon,
  },
  {
    id: PROFILE_ADD_INTENTS.DOCUMENT,
    label: "Document or photo",
    description: "Add a care document, photo, or screenshot.",
    icon: PhotoCameraOutlinedIcon,
  },
];

const UniversalProfileAddSheet = ({
  open,
  onClose,
  onRouteIntent,
  childId,
  childName,
  accent,
  initialIntent = "",
  captureTitle: captureTitleProp,
  capturePlaceholder,
  profileChapterId = "",
  profileChapterName = "",
  profileField = "",
  profilePlacementMode = "",
}) => {
  const [captureIntent, setCaptureIntent] = useState("");
  const [explicitSaveConfirmation, setExplicitSaveConfirmation] = useState(null);

  const resolvedInitialIntent = initialIntent || "";

  React.useEffect(() => {
    if (open) {
      setCaptureIntent(resolvedInitialIntent);
      setExplicitSaveConfirmation(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setCaptureIntent("");
    setExplicitSaveConfirmation(null);
    onClose?.();
  };

  React.useEffect(() => {
    if (!explicitSaveConfirmation) return undefined;
    const closeTimer = window.setTimeout(handleClose, 1200);
    return () => window.clearTimeout(closeTimer);
  // `handleClose` intentionally reflects the current onClose callback.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explicitSaveConfirmation]);

  const handleIntent = (intent) => {
    if (intent === PROFILE_ADD_INTENTS.KNOWLEDGE || intent === PROFILE_ADD_INTENTS.DOCUMENT) {
      setCaptureIntent(intent);
      return;
    }
    onRouteIntent?.(intent);
    handleClose();
  };

  const captureTitle = captureTitleProp
    || (captureIntent === PROFILE_ADD_INTENTS.DOCUMENT ? "Add a document or photo" : "What should caregivers know?");

  if (!open) {
    return null;
  }

  return (
    <LogFormShell
      open={open}
      onClose={handleClose}
      title={captureIntent ? captureTitle : "Add"}
      subtitle={captureIntent ? "Keep the original wording and source together." : "Choose where this belongs."}
      maxWidth="sm"
      forceDrawer
    >
      {captureIntent ? (
        <Stack spacing={1.25}>
          {explicitSaveConfirmation ? (
            <>
              <Alert severity={explicitSaveConfirmation.severity || "success"}>
                {explicitSaveConfirmation.message}
              </Alert>
            </>
          ) : (
            <>
              <CareProfileBackButton
                onClick={() => setCaptureIntent("")}
                ariaLabel="Back to Add"
                label="Back to Add"
              />
              <CareKnowledgeCaptureCard
                childId={childId}
                childName={childName}
                accent={accent}
                title={captureTitle}
                placeholder={capturePlaceholder || "Type, paste, or dictate what caregivers should know..."}
                helperText="We’ll keep your wording and organize it for review."
                profileChapterId={profileChapterId}
                profileChapterName={profileChapterName}
                profileField={profileField}
                profilePlacementMode={profilePlacementMode}
                onExplicitProfileTextSaved={setExplicitSaveConfirmation}
                onExplicitProfileSourceSaved={setExplicitSaveConfirmation}
              />
            </>
          )}
        </Stack>
      ) : (
        <Stack spacing={0.9}>
          {ADD_OPTIONS.map(({ id, label, description, icon: Icon }) => (
            <Button
              key={id}
              variant="outlined"
              onClick={() => handleIntent(id)}
              aria-label={label}
              sx={{
                minHeight: 70,
                justifyContent: "flex-start",
                gap: 1.2,
                px: 1.4,
                py: 1,
                borderRadius: "16px",
                borderColor: colors.semantic.line,
                color: colors.semantic.ink,
                textAlign: "left",
                textTransform: "none",
                "&:hover": { borderColor: colors.brand.deep, bgcolor: alpha(colors.brand.deep, 0.04) },
              }}
            >
              <Box sx={{ display: "grid", placeItems: "center", width: 38, height: 38, borderRadius: "12px", bgcolor: alpha(colors.brand.deep, 0.09), color: colors.brand.deep, flexShrink: 0 }}>
                <Icon fontSize="small" />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 850, lineHeight: 1.2 }}>{label}</Typography>
                <Typography sx={{ mt: 0.2, fontSize: "0.8rem", lineHeight: 1.35, color: alpha(colors.semantic.ink, 0.65) }}>
                  {description}
                </Typography>
              </Box>
            </Button>
          ))}
        </Stack>
      )}
    </LogFormShell>
  );
};

export const ProfileAddButton = ({ onClick }) => (
  <Button
    variant="outlined"
    onClick={onClick}
    startIcon={<AddRoundedIcon />}
    sx={{
      alignSelf: "center",
      minWidth: 132,
      borderRadius: 999,
      textTransform: "none",
      fontWeight: 850,
      borderColor: colors.semantic.line2,
      color: colors.brand.deep,
      bgcolor: "#FFFFFF",
    }}
  >
    Add
  </Button>
);

export default UniversalProfileAddSheet;
